import React, { useEffect, useState } from 'react';
import { apiService } from 'services/base.service';
import { CokitchenPaymentSettingsType } from 'utils/types';
import { Breadcrumbs, Switch, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import useDashboardContext from 'contexts/dashboard.context';
import PaystackIcon from 'assets/icons/paystack.svg';
import ChipperCashIcon from 'assets/icons/chipper_cash.svg';
import OkraIcon from 'assets/icons/okra.svg';
import ThePeerIcon from 'assets/icons/thepeer.svg';
import BankTransferIcon from 'assets/icons/bank_transfer.svg';
type PaymentMethod = {
  name: string;
  icon: string;
  key: keyof CokitchenPaymentSettingsType;
};

const paymentSettingsArray: PaymentMethod[] = [
  {
    name: 'Paystack',
    icon: PaystackIcon,
    key: 'paystack_card'
  },
  {
    name: 'The Peer Wallet',
    icon: ThePeerIcon,
    key: 'thepeer_wallet'
  },
  {
    name: 'Okra',
    icon: OkraIcon,
    key: 'okra'
  },
  {
    name: 'Chipper Cash',
    icon: ChipperCashIcon,
    key: 'show_chipper_logo'
  },
  {
    name: 'Bank Transfer',
    icon: BankTransferIcon,
    key: 'transfer'
  }
];

const PaymentSettings = () => {
  const breadCrumbs = [
    { name: 'Settings', path: `/settings` },
    {
      name: 'Payment',
      path: ''
    }
  ];

  const { activeCokitchen } = useDashboardContext();
  const [paymentSettingsObject, setPaymentSettingsObject] = useState<CokitchenPaymentSettingsType>(
    {} as CokitchenPaymentSettingsType
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeCokitchen.id) getSettings();
  }, [activeCokitchen.id]);

  const getSettings = async () => {
    try {
      const res = await apiService.get(`marketing/cokitchen/settings/${activeCokitchen?.id}`, {
        isInternal: true
      });
      const { data } = res.data;
      setPaymentSettingsObject({
        okra: data.okra,
        paystack_card: data.paystack_card,
        thepeer_wallet: data.thepeer_wallet,
        show_chipper_logo: data.show_chipper_logo,
        transfer: data.transfer
      });
    } catch (error) {
      console.log(error);
    }
  };

  const updatePaymentSetting = async (key: keyof CokitchenPaymentSettingsType) => {
    try {
      setLoading(true);
      const updatedValue = {
        ...paymentSettingsObject,
        [key]: !paymentSettingsObject[key]
      };

      await apiService.put(`marketing/cokitchen/settings`, {
        data: {
          ...updatedValue,
          cokitchen_id: activeCokitchen.id
        }
      });
      setPaymentSettingsObject(updatedValue);
      toast(<ToastMessage message='Payment settings updated successfully' type='success' />);
    } catch (error) {
      toast(<ToastMessage message='Error updating payment settings' type='error' />);
      getSettings();
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <Breadcrumbs breadcrumbs={breadCrumbs} />

      <div className='w-full flex justify-center items-center mt-8'>
        <div className='w-full max-w-[514px] shadow py-6 px-[30px]'>
          <h6 className='text-[24px] font-semibold'>Payment</h6>
          <p className='text-gray-5 font-medium text-sm mb-8'>
            Manage payment options on the FoodCourt App
          </p>
          {paymentSettingsArray.map((payment) => (
            <div
              className='flex justify-between w-full rounded-md border-[0.8px] border-[#EAEBEB] h-[68px] p-5 mb-4'
              key={payment.name}>
              <div className='flex gap-4'>
                <img src={payment.icon} alt='payment icon' />
                <p className='font-medium'>{payment.name}</p>
              </div>
              <Switch
                active={paymentSettingsObject[payment.key] ?? false}
                onChange={() => {
                  setPaymentSettingsObject((prev) => ({
                    ...prev,
                    [payment.key]: !prev[payment.key]
                  }));
                  updatePaymentSetting(payment.key);
                }}
                disabled={loading}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;
